import React from "react";
import { PublicLayout, Eyebrow, SectionCTA, ReferralPreview } from "@/components/public/PublicKit";
import { CheckCircle2, Filter, History, ShieldCheck } from "lucide-react";

const STAGES = ["Submitted", "Contacted", "Accepted", "In Progress", "Completed"];
const FIELDS = [
  "Referring client / practice", "Service", "Receiving organization or partner",
  "Assigned owner", "Referral date", "Internal reference number",
  "Current status", "Next follow-up date", "Status history",
];

export default function ReferralTrackingInfo() {
  return (
    <PublicLayout>
      <section className="border-b border-slate-100 bg-slate-50">
        <div className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-20">
          <Eyebrow>Referral Tracking</Eyebrow>
          <h1 className="max-w-3xl font-display text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">Follow every referral from submission through completion</h1>
          <p className="mt-4 max-w-2xl text-lg text-slate-500">A dedicated workspace to track the referrals your clients and practices send — with clear ownership, follow-up dates and a full status history.</p>
        </div>
      </section>

      {/* Workflow */}
      <section className="mx-auto max-w-7xl px-5 py-14 lg:px-8">
        <h2 className="font-display text-2xl font-bold text-slate-900">A simple, visible workflow</h2>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              <span className="rounded-full border border-primary/30 bg-primary/5 px-4 py-2 text-sm font-bold text-primary">{s}</span>
              {i < STAGES.length - 1 && <span className="text-slate-300">→</span>}
            </React.Fragment>
          ))}
          <span className="ml-2 rounded-full bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-500">+ Declined / Cancelled</span>
        </div>

        <div className="mt-10 grid items-start gap-10 lg:grid-cols-2">
          <div>
            <h3 className="font-display text-xl font-bold text-slate-900">What every referral captures</h3>
            <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
              {FIELDS.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-slate-600"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><span>{f}</span></li>
              ))}
            </ul>
            <div className="mt-6 space-y-3">
              <Feature icon={Filter} title="Filter the way you work" desc="By client, service, receiving partner, owner, status and date range." />
              <Feature icon={History} title="Full accountability" desc="Record who changed a status and when, shown on the client's timeline." />
              <Feature icon={ShieldCheck} title="Operational, not clinical" desc="Use internal references for tracking; keep patient-identifying data in your approved clinical system." />
            </div>
          </div>
          <ReferralPreview />
        </div>
      </section>

      <SectionCTA />
    </PublicLayout>
  );
}

function Feature({ icon: Icon, title, desc }) {
  return (
    <div className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Icon className="h-5 w-5" /></span>
      <div><p className="font-bold text-slate-800">{title}</p><p className="text-sm text-slate-500">{desc}</p></div>
    </div>
  );
}
